import React, { useState, useCallback, useEffect } from "react";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import InfoIcon from "@mui/icons-material/InfoOutlined";

import AmountComponent from "../AmountComponent";
import SeeParkingRates from "../SeeParkingRates";
import { CurrencyTextField } from "@app.automotus.io/components/CurrencyTextField";
import { formatCurrency } from "common/format";

const DEFAULT_AMOUNT = 1500;
const IndividualDriverAmountData = [
  {
    amount: 500,
    description: "approx. 10 parks",
  },
  {
    amount: DEFAULT_AMOUNT,
    description: "approx. 30 parks",
    isRecommended: true,
  },
  {
    amount: 5000,
    description: "approx. 100 parks",
  },
];

const FleetOperatorAmountData = [
  {
    amount: 10000,
    description: "approx. 200 parks",
  },
  {
    amount: 25000,
    description: "approx. 500 parks",
    isRecommended: true,
  },
  {
    amount: 100000,
    description: "approx. 2000 parks",
  },
];

export enum LastTouchedStatus {
  None,
  Option,
  Other,
}

/**
 * Component that renders a select amount view.
 */
export const SelectAmount: React.FC<SelectAmountProps> = ({
  paymentPreference,
  onChange,
  amount,
  disabled,
  isBillByMail = false,
  minAmount = 500,
  maxAmount = 10000,
  isFleetOperator = false,
}: SelectAmountProps) => {
  const theme = useTheme();
  const [otherAmount, setOtherAmount] = useState<number | null>(null);
  const [lastTouched, setLastTouched] = useState<LastTouchedStatus>(LastTouchedStatus.Option);
  const [lastSelectedItemAmount, setLastSelectedItemAmount] = useState<number>(DEFAULT_AMOUNT);

  const AmountData = isFleetOperator ? FleetOperatorAmountData : IndividualDriverAmountData;

  const onOtherAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setLastTouched(LastTouchedStatus.Other);
      if (e.target.value === "") {
        setOtherAmount(null);
        onChange(lastSelectedItemAmount, LastTouchedStatus.Option);
        setLastTouched(LastTouchedStatus.Option);
      } else {
        const newAmount: number = +e.target.value;
        setOtherAmount(newAmount);
        onChange(newAmount * 100, LastTouchedStatus.Other);
      }
    },
    [setOtherAmount, onChange, lastSelectedItemAmount],
  );

  useEffect(() => {
    if (amount && !AmountData.some(({ amount: itemAmount }) => amount === itemAmount)) {
      setLastTouched(LastTouchedStatus.Other);
      setOtherAmount(amount / 100);
    }
  }, [amount, AmountData]);

  const isInvalidAmount = otherAmount !== null && (amount < minAmount || amount > maxAmount);

  return (
    <Box sx={{ width: "100%" }}>
      {paymentPreference === "wallet_balance" && (
        <>
          <Typography variant="subtitle2">Select Amount</Typography>
          <Typography variant="body4" sx={{ fontSize: "0.75rem" }} color={theme.palette.text.secondary}>
            approx. parks based on average park time of 6 min.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 1, mt: 0.5 }}>
            {AmountData.map(({ amount: itemAmount, description, isRecommended }) => (
              <Box
                key={itemAmount}
                sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", width: "33%" }}
              >
                <AmountComponent
                  amount={itemAmount}
                  description={description}
                  isSelected={lastTouched === LastTouchedStatus.Option && itemAmount === amount}
                  onSelect={() => {
                    if (disabled) {
                      return;
                    }
                    onChange(itemAmount, LastTouchedStatus.Option);
                    setOtherAmount(null);
                    setLastTouched(LastTouchedStatus.Option);
                    setLastSelectedItemAmount(itemAmount);
                  }}
                />
                {isRecommended && (
                  <Typography
                    variant="body4"
                    color={theme.palette.text.secondary}
                    sx={{ fontSize: "10px", fontWeight: 400, fontStyle: "italic" }}
                  >
                    Most Popular
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
          {!isBillByMail && (
            <CurrencyTextField
              fullWidth
              error={isInvalidAmount}
              helperText={
                isInvalidAmount
                  ? `Enter an amount between $${formatCurrency(minAmount)} and $${formatCurrency(maxAmount)}`
                  : " "
              }
              value={otherAmount === 0 || otherAmount ? otherAmount : ""}
              label={`Other ($${formatCurrency(minAmount)} minimum)`}
              onChange={onOtherAmountChange}
            />
          )}
        </>
      )}
      {!isBillByMail && <SeeParkingRates />}
      {!isBillByMail && lastTouched === LastTouchedStatus.Option && amount === 0 && (
        <Alert
          icon={<InfoIcon sx={{ fontSize: "20px", color: "#ED6C02" }} />}
          severity="warning"
          sx={{ mb: 3, color: "#ED6C02", alignItems: "center", backgroundColor: "rgba(237, 108, 2, 0.08)" }}
        >
          By selecting 'try for free' your CurbPass won't be loaded until after your next park.
        </Alert>
      )}
    </Box>
  );
};

type SelectAmountProps = {
  paymentPreference: "wallet_balance" | "session";
  amount: number;
  onChange: (value: number, type: number) => void;
  disabled: boolean;
  isBillByMail?: boolean;
  minAmount?: number;
  maxAmount?: number;
  isFleetOperator?: boolean;
};

export default SelectAmount;
